import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type DocumentType = "Factura" | "Packing List" | "Orden de Compra" | "Otro";

export type EstadoDocumento = "Pendiente" | "Procesando" | "Pagado" | "Completado" | "Cancelado";

export interface DocumentRecord {
  id: string;
  created_at: string;
  id_correo: string;
  fecha: string;
  tipo_documento: string;
  no_documento: string;
  emisor: string;
  receptor: string;
  moneda: string;
  total_factura: number;
  costo_flete: number;
  seguro: number;
  impuestos: number;
  descuento_ajuste: number;
  otros_cargos: number;
  origen: string;
  destino: string;
  incoterm: string;
  notas_tecnicas: string;
  archivo_url: string | null;
  estado: EstadoDocumento;
  // Datos logísticos — nullable para distinguir "sin dato OCR" de "0 declarado"
  peso_bruto_kg: number | null;
  peso_neto_kg: number | null;
  volumen_cbm: number | null;
  total_piezas: number | null;
  embarque_id: string | null;
  embarque_numero_do: string | null;
  datos_extra: {
    total_piezas?: number;
    peso_bruto_kg?: number;
    volumen_cbm?: number;
    [key: string]: any;
  } | null;
}

export interface InventoryItem {
  id: string;
  documento_id: string;
  id_correo: string;
  no_documento: string;
  item_no: number;
  sku: string;
  hs_code: string;
  descripcion: string;
  cantidad: number;
  unidad_medida: string;
  precio_unitario: number;
  sub_total: number;
  nota_item: string;
  peso_bruto_kg: number;
  iva_override: number | null;
  aplica_tlc: boolean;
  /** B-02B — régimen aduanero del ítem; default 'GENERAL' en DB. */
  beneficio_aduanero?: "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA" | null;
  precio_venta_esperado: number;
  hs_code_deducido_por_ia?: boolean;
  /** ICO — Impuesto al Consumo (bebidas alcohólicas / vehículos / cigarrillos). */
  grados_alcoholicos?: number | null;
  litros_por_unidad?: number | null;
  tarifa_ico?: number | null;
  /** Tipo de ICO: null/'BEBIDAS_ALCOHOLICAS' (legacy g·t·l), 'VEHICULOS' (% sobre CIF), 'CIGARRILLOS'. */
  ico_tipo?: string | null;
}

export const COP_RATE = 4150;

export function useDocuments(tipoDocumento?: string) {
  const { user, orgId } = useAuth();

  return useQuery({
    queryKey: ["documents", orgId, tipoDocumento],
    queryFn: async () => {
      let query = supabase
        .from("registro_documentos")
        .select("*, embarques(numero_do)")
        .eq("org_id", orgId!)
        .is("deleted_at", null);
      if (tipoDocumento) {
        query = query.eq("tipo_documento", tipoDocumento);
      }
      const { data, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((d: any) => ({
        ...d,
        embarque_numero_do: d.embarques?.numero_do ?? null,
      })) as DocumentRecord[];
    },
    enabled: !!user && !!orgId,
  });
}

export function useInventory(documentIds?: string[]) {
  const { user, orgId } = useAuth();

  return useQuery({
    queryKey: ["inventory", orgId, documentIds],
    queryFn: async () => {
      if (documentIds && documentIds.length === 0) {
        return [] as InventoryItem[];
      }
      let query = supabase.from("base_inventario").select("*").eq("org_id", orgId!).is("deleted_at", null);
      if (documentIds && documentIds.length > 0) {
        query = query.in("documento_id", documentIds);
      }
      const { data, error } = await query.order("item_no", { ascending: true });
      if (error) throw error;
      return (data ?? []) as InventoryItem[];
    },
    enabled: !!user && !!orgId,
  });
}

export function useAllDocuments() {
  const { user, orgId } = useAuth();

  return useQuery({
    queryKey: ["all-documents", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("registro_documentos")
        .select("*, embarques(numero_do)")
        .eq("org_id", orgId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((d: any) => ({
        ...d,
        embarque_numero_do: d.embarques?.numero_do ?? null,
      })) as DocumentRecord[];
    },
    enabled: !!user && !!orgId,
  });
}
